import {
  HttpEvent,
  HttpEventType,
  HttpStatusCode,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { constantVariables } from '@shared/helper/configs';
import { IError } from '@shared/models/general.interface';
import { GeneralService } from '@shared/services/general.service';
import { SnackbarService, SnackbarType } from '@shared/services/snackbar.service';
import { TokenService } from '@shared/services/token.service';
import { BehaviorSubject, Observable, catchError, filter, map, switchMap, take } from 'rxjs';

let isRefreshing = false;
let gettingVisitorId = false;
const refreshTokenSubject = new BehaviorSubject<any>(null);
const visitorIdSubject = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const generalService = inject(GeneralService);
  const snackbarService = inject(SnackbarService);

  return next(req).pipe(
    map((res) => {
      if (res.type === HttpEventType.Response) {
        if (res?.body) {
          const operationName = (req.body as any)?.operationName
            ? (req.body as any).operationName
            : '';
          const errors = (res.body as any).errors;
          if (errors) {
            let presentedError: IError;
            presentedError = {
              operationName,
              error: errors[0].error,
              message: errors[0].message,
              metadata: errors[0].metadata,
              statusCode: errors[0].statusCode,
            };
            throw presentedError;
          }
        }
      }
      return res;
    }),
    catchError((error: any) => {
      if (error) {
        if (
          error.statusCode === HttpStatusCode.Unauthorized ||
          error.status === HttpStatusCode.Unauthorized
        ) {
          if (['refreshToken', 'logout'].includes(error.operationName)) {
            throw error;
          }
          return handle401Error(generalService, tokenService, req, next);
        } else if (
          error.statusCode === HttpStatusCode.Locked ||
          error.status === HttpStatusCode.Locked
        ) {
          return handle423Error(generalService, tokenService, req, next);
        }
        snackbarService.showSnackbar(error.message, SnackbarType.error);
      }
      throw error;
    }),
  );
};

const handle401Error = (
  generalService: GeneralService,
  tokenService: TokenService,
  request: HttpRequest<any>,
  next: {
    (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>>;
    (arg0: HttpRequest<any>): any;
  },
) => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return tokenService.refreshToken().pipe(
      switchMap((data) => {
        if (data.data?.refreshToken?.access_token && data.data.refreshToken.refresh_token) {
          isRefreshing = false;
          if (generalService.isBrowserOnly)
            refreshTokenSubject.next(localStorage.getItem(constantVariables.accessToken));

          return next(request);
        } else {
          tokenService.logout().subscribe();
          return next(request);
        }
      }),
      catchError((err) => {
        isRefreshing = false;
        tokenService.logout().subscribe();
        throw err;
      }),
    );
  }

  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap(() => next(request)),
  );
};

const handle423Error = (
  generalService: GeneralService,
  tokenService: TokenService,
  request: HttpRequest<any>,
  next: {
    (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>>;
    (arg0: HttpRequest<any>): any;
  },
) => {
  if (!gettingVisitorId) {
    gettingVisitorId = true;
    visitorIdSubject.next(null);

    return tokenService.getVisitorId().pipe(
      switchMap((data) => {
        if (data.data?.getVisitorId) {
          gettingVisitorId = false;
          if (tokenService.getRtk()) {
            tokenService.logout().subscribe();
          }
          if (generalService.isBrowserOnly)
            visitorIdSubject.next(localStorage.getItem(constantVariables.visitorId));
          return next(request);
        } else {
          if (tokenService.getRtk()) {
            tokenService.logout().subscribe();
          }
          return next(request);
        }
      }),
      catchError((err) => {
        gettingVisitorId = false;
        if (tokenService.getRtk()) {
          tokenService.logout().subscribe();
        }
        throw err;
      }),
    );
  }

  return visitorIdSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap(() => next(request)),
  );
};
